import { DailyLog, UserProfile, InviteCode, UserBasic } from "@/types";

const API_BASE = process.env.NEXT_PUBLIC_API_BASE || "/api";
const IS_OFFLINE = process.env.NEXT_PUBLIC_OFFLINE_MODE === "true";


const LOGS_KEY = "longevity_logs";
const PROFILE_KEY = "longevity_profile";
const USER_KEY = "longevity_user_auth";
const USER_INFO_KEY = "longevity_user_info";
const TOKEN_KEY = "longevity_token";

// Sync version - returns cached data immediately
export const getLogs = (): DailyLog[] => {
    if (typeof window === "undefined") return [];
    const stored = localStorage.getItem(LOGS_KEY);
    return stored ? JSON.parse(stored) : [];
};

// Async version - fetches from backend and updates cache
export const fetchLogs = async (): Promise<DailyLog[]> => {
    if (typeof window === "undefined") return [];
    if (IS_OFFLINE) return getLogs();
    const token = getToken();

    if (!token) return getLogs();

    try {
        const res = await fetch(`${API_BASE}/logs`, {
            headers: { Authorization: `Bearer ${token}` },
            cache: "no-store"
        });
        if (res.ok) {
            const json = await res.json();
            if (json?.logs) {
                const logs = json.logs.map((item: { data: DailyLog }) => item.data);
                localStorage.setItem(LOGS_KEY, JSON.stringify(logs));
                return logs;
            } else {
                localStorage.removeItem(LOGS_KEY);
                return [];
            }
        }
        return getLogs();
    } catch {
        return getLogs();
    }
};

export const saveLog = async (log: DailyLog) => {
    if (typeof window === "undefined") return;
    if (typeof window === "undefined") return;

    const token = getToken();


    // Optimistically update local cache
    const logs = getLogs();
    const existingIndex = logs.findIndex((l) => l.date === log.date);
    if (existingIndex >= 0) logs[existingIndex] = log; else logs.push(log);
    logs.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    localStorage.setItem(LOGS_KEY, JSON.stringify(logs));

    if (IS_OFFLINE) return;

    if (!token) return;
    try {
        await fetch(`${API_BASE}/logs`, {

            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ date: log.date, data: log }),
        });
    } catch (err) {
        console.error("Failed to save log to backend:", err);
    }
};

export const saveUserProfile = async (profile: UserProfile) => {
    if (typeof window === "undefined") return;
    if (typeof window === "undefined") return;

    const token = getToken();


    // Optimistically update local cache
    localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));

    if (IS_OFFLINE) return;

    if (!token) return;
    try {
        await fetch(`${API_BASE}/profile`, {

            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(profile),
        });
    } catch (err) {
        console.error("Failed to save profile to backend:", err);
    }
};

export const deleteUserProfile = async () => {
    if (typeof window === "undefined") return;

    const token = getToken();

    // Call backend FIRST while token is still valid
    if (!IS_OFFLINE && token) {
        try {
            await fetch(`${API_BASE}/profile`, {
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
        } catch (err) {
            console.error("Failed to delete profile from backend:", err);
        }
    }

    // Clear ALL local storage AFTER the backend call
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(PROFILE_KEY);
    localStorage.removeItem(LOGS_KEY);
    localStorage.removeItem(USER_INFO_KEY);
};

// Sync version - returns cached data immediately
export const getUserProfile = (): UserProfile | null => {
    if (typeof window === "undefined") return null;
    const stored = localStorage.getItem(PROFILE_KEY);
    return stored ? JSON.parse(stored) : null;
};

// Async version - fetches from backend and updates cache
export const fetchUserProfile = async (): Promise<UserProfile | null> => {
    if (typeof window === "undefined") return null;
    if (IS_OFFLINE) return getUserProfile();
    const token = getToken();
    if (!token) return getUserProfile();


    try {
        const res = await fetch(`${API_BASE}/profile`, {
            headers: { Authorization: `Bearer ${token}` },
            cache: "no-store"
        });
        if (res.ok) {
            const json = await res.json();
            if (json?.profile) {
                localStorage.setItem(PROFILE_KEY, JSON.stringify(json.profile));
                return json.profile;
            } else {
                localStorage.removeItem(PROFILE_KEY);
                return null;
            }
        }
        return getUserProfile();
    } catch {
        return getUserProfile();
    }
};

export const loginUser = (mobile: string) => {
    if (typeof window === "undefined") return;
    localStorage.setItem(USER_KEY, JSON.stringify({ mobile, isLoggedIn: true }));
};

export const storeToken = (token: string) => {
    if (typeof window === "undefined") return;
    localStorage.setItem(TOKEN_KEY, token);
};

export const getToken = (): string | null => {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(TOKEN_KEY);
};

export const isUserLoggedIn = (): boolean => {
    if (typeof window === "undefined") return false;
    const stored = localStorage.getItem(USER_KEY);
    return stored ? JSON.parse(stored).isLoggedIn : false;
};

export const logoutUser = () => {
    if (typeof window === "undefined") return;
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_INFO_KEY);
};

// User info helpers
export const storeUserInfo = (user: UserBasic) => {
    if (typeof window === "undefined") return;
    localStorage.setItem(USER_INFO_KEY, JSON.stringify(user));
};

export const getUserInfo = (): UserBasic | null => {
    if (typeof window === "undefined") return null;
    const stored = localStorage.getItem(USER_INFO_KEY);
    return stored ? JSON.parse(stored) : null;
};

export const fetchCurrentUser = async (): Promise<UserBasic | null> => {
    if (IS_OFFLINE) return getUserInfo();
    const token = getToken();
    if (!token) return getUserInfo();

    try {
        const res = await fetch(`${API_BASE}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
            cache: "no-store",
        });
        if (res.ok) {
            const json = await res.json();
            const user: UserBasic | null = json?.user ?? null;
            if (user) {
                storeUserInfo(user);
                return user;
            } else {
                localStorage.removeItem(USER_INFO_KEY);
                return null;
            }
        }
        return getUserInfo();
    } catch {
        return getUserInfo();
    }
};


// Invite code helpers
export const fetchMyInviteCodes = async (): Promise<InviteCode[]> => {
    const token = getToken();
    if (!token) return [];
    try {
        const res = await fetch(`${API_BASE}/invites/my-codes`, {
            headers: { Authorization: `Bearer ${token}` },
            cache: "no-store",
        });
        const json = await res.json();
        return json?.inviteCodes ?? [];
    } catch {
        return [];
    }
};

export const createInviteCode = async (usageLimit: number, expiryDays?: number): Promise<InviteCode | null> => {
    const token = getToken();
    if (!token) return null;
    try {
        const res = await fetch(`${API_BASE}/invites`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ usageLimit, expiryDays }),
        });
        const json = await res.json();
        return json?.inviteCode ?? null;
    } catch {
        return null;
    }
};

export const deactivateInviteCode = async (inviteCodeId: string): Promise<boolean> => {
    const token = getToken();
    if (!token) return false;
    try {
        const res = await fetch(`${API_BASE}/invites/${inviteCodeId}/deactivate`, {
            method: "PUT",
            headers: { Authorization: `Bearer ${token}` },
        });
        return res.ok;
    } catch {
        return false;
    }
};

export const fetchUsersByInviteCode = async (inviteCodeId: string): Promise<UserBasic[]> => {
    const token = getToken();
    if (!token) return [];
    try {
        const res = await fetch(`${API_BASE}/invites/users/${inviteCodeId}`, {
            headers: { Authorization: `Bearer ${token}` },
            cache: "no-store",
        });
        const json = await res.json();
        return json?.users ?? [];
    } catch {
        return [];
    }
};

export const updateUserRole = async (userId: string, role: "user" | "admin" | "superadmin") => {
    const token = getToken();
    if (!token) return null;
    try {
        const res = await fetch(`${API_BASE}/users/${userId}/role`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ role }),
        });
        if (!res.ok) return null;
        const json = await res.json();
        return json?.user ?? null;
    } catch {
        return null;
    }
};

export const fetchAICoachAnalysis = async (mode: 'daily' | 'weekly', profile: UserProfile, data: { log?: DailyLog; logs?: DailyLog[] }) => {
    // NOTE: We now use the local Next.js API route instead of the external backend.
    // This allows AI analysis to work even if the backend server (5001) is down,
    // as long as the Next.js server has the OPENAI_API_KEY.

    // We do NOT block this in offline mode anymore, because the local server *is* the backend.

    try {
        const response = await fetch('/api/ai-coach', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                // Authorization is usually not needed for internal API routes unless middleware requires it
            },
            body: JSON.stringify({
                mode,
                profile,
                log: data.log,   // Flattened for compatibility with backend (Express)
                logs: data.logs, // Flattened for compatibility with backend (Express)
                data // Keeping temporarily for backward compatibility if needed by other consumers
            }),
        });

        // Debug Log
        console.log("Saving log via AI Coach API:", JSON.stringify({ mode, profile, data }).slice(0, 200) + "...");


        if (!response.ok) {
            let errorMessage = response.statusText;
            try {
                const errorData = await response.json();
                if (errorData?.error) errorMessage = errorData.error;
            } catch {
                // Ignore JSON parse error, use statusText
            }
            throw new Error(`AI Coach API failed: ${errorMessage}`);
        }

        return await response.json();
    } catch (error) {
        console.error("AI Coach Error:", error);
        throw error;
    }
};
